import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { api, unwrap } from "@/api/client";
import { qk } from "@/api/keys";
import { meQueryOptions } from "@/auth/me";
import { useSession } from "@/auth/session";
import { AppShell } from "@/components/app-shell";
import { LoadingBlock } from "@/components/ui/states";
import {
  AcceptInvitationPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  SignInPage,
  SignUpPage,
  VerifyEmailPage,
} from "@/routes/auth";
import { DashboardPage } from "@/routes/dashboard";
import { DeviceDetailPage } from "@/routes/device-detail";
import { DevicesPage } from "@/routes/devices";
import { MonitoringPage } from "@/routes/monitoring";
import {
  CreateOrganizationPage,
  FirstSitePage,
  InviteTeamPage,
} from "@/routes/onboarding";
import { NewRefillPage, RefillsPage } from "@/routes/refills";
import { ReportsPage } from "@/routes/reports";
import { RequestsPage } from "@/routes/requests";
import { SiteDetailPage, SitesPage } from "@/routes/sites";
import { SettingsPage } from "@/routes/settings";

/**
 * The route tree, with the guards in `beforeLoad`.
 *
 * A guard runs BEFORE the route's component is created, so a route that needs an
 * organization never renders for a fraction of a second without one — no flash of an
 * empty dashboard, no hook that throws because `activeOrgId` was undefined.
 *
 * The guards read `/v1/me` out of the SAME query cache the components use
 * (`meQueryOptions`), so the router and the UI can never disagree about who you are.
 */

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
});

/* --------------------------------------------------------------- public */

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_public",
  component: Outlet,
});

const signInRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search["redirect"] === "string" ? { redirect: search["redirect"] } : {},
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/sign-up",
  component: SignUpPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" ? { token: search["token"] } : {},
  component: ResetPasswordPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" ? { token: search["token"] } : {},
  component: VerifyEmailPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/invitations",
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" ? { token: search["token"] } : {},
  component: AcceptInvitationPage,
});

/* ---------------------------------------------------------- onboarding */

/**
 * Signed in, but not necessarily in an organization — this is the ONE place that is true.
 * A user who has just registered has an identity and nothing else.
 */
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_onboarding",
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me) {
      throw redirect({ to: "/sign-in", search: { redirect: location.href } });
    }
  },
  component: Outlet,
});

const createOrgRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/organization",
  component: CreateOrganizationPage,
});

const inviteTeamRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/team",
  component: InviteTeamPage,
});

const firstSiteRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/site",
  component: FirstSitePage,
});

/* ----------------------------------------------------------- the app */

/**
 * Everything below here REQUIRES an active organization. That is what makes it safe for
 * `useActiveOrgId()` to throw rather than return undefined: by the time any of these
 * components mount, the guard has already proved there is one.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: async ({ context, location }) => {
    // fetchQuery, not ensureQueryData.
    //
    // ensureQueryData hands back whatever is in the cache, even when it is STALE. For an
    // auth guard that is wrong by definition: the one question this function exists to
    // answer is "who are you, right now". Serving a cached "not signed in" from the moment
    // before the user signed in is how this guard raced the post-login refetch and bounced
    // people straight back to the sign-in page they had just used.
    let me = await context.queryClient.fetchQuery(meQueryOptions);

    if (!me) {
      throw redirect({ to: "/sign-in", search: { redirect: location.href } });
    }

    // NO ACTIVE ORGANIZATION IS NOT THE SAME AS NO ORGANIZATION.
    //
    // The server does not pick a tenant for you at sign-in — a session carries WHO you are
    // and, separately, WHICH ORG YOU ARE ACTING IN, and the second is an explicit choice
    // (a consultant who works for three banks must never be ambiguous about which one they
    // just recorded a refill for). So `active_organization` is null on every fresh login,
    // for everybody.
    //
    // This branch used to read that as "belongs to no organization" and send the user to
    // create one. Which meant a returning customer with three branches, signing in on a
    // Monday, was told to create an organization they already had. The mock hid it by
    // auto-selecting an org at sign-in — a mock kinder than the server, telling us the
    // product worked when it did not.
    //
    // A member with no active org gets BOUND to one. Only a member of nothing goes to
    // onboarding.
    if (!me.active_organization) {
      const first = me.memberships?.[0]?.organization;

      if (!first) {
        // Registered, verified, and a member of nothing. This is the genuine first run.
        throw redirect({ to: "/onboarding/organization" });
      }

      await unwrap(
        api.POST("/v1/organizations/{orgId}/switch", {
          params: { path: { orgId: first.id } },
        }),
      );

      // Re-read identity from the server rather than patching the cache by hand: the
      // session's active org lives server-side, and /v1/me is the only thing entitled to
      // say what it now is.
      await context.queryClient.invalidateQueries({ queryKey: qk.me() });
      me = await context.queryClient.fetchQuery(meQueryOptions);

      if (!me?.active_organization) {
        throw redirect({ to: "/onboarding/organization" });
      }
    }
  },
  component: AppRoot,
});

/**
 * THE GATE. Nothing tenant-scoped mounts until the session context ACTUALLY HOLDS the org.
 *
 * The guard above has already proved the org exists — it fetched /v1/me, and for a member
 * with no bound tenant it called /switch and re-read it. But `beforeLoad` proves a fact
 * about the SERVER; this proves the fact has arrived in REACT, and those are not the same
 * instant.
 *
 * The router commits this component in the same tick the guard finishes writing the query
 * cache, which can be before `SessionProvider` above has re-rendered with it. Any child that
 * calls `useActiveOrgId()` in that window used to throw — and React's error boundary LATCHES:
 * the data landed a millisecond later and the screen stayed dead, showing "Something went
 * wrong" forever. That is why every screen worked when you navigated to it and only the first
 * render after sign-in was broken.
 *
 * I chased that race through three layers — the guard, the provider, the hook — patching the
 * place it happened to surface each time. That was wrong. A component that throws during a
 * transient will always find a new way to latch the boundary. So: DO NOT RENDER IT DURING
 * THE TRANSIENT. One tick of a loading state, and the invariant becomes true before anything
 * can depend on it.
 *
 * `useActiveOrgId`'s throw goes back to meaning what it should: a genuine bug — a
 * tenant-scoped hook on a route the guard never should have admitted — rather than a race a
 * dozen components each have to survive.
 */
function AppRoot() {
  const { me, activeOrgId } = useSession();
  const navigate = useNavigate();

  /**
   * THE OTHER HALF OF SESSION EXPIRY.
   *
   * `beforeLoad` above already proved there was a session at the moment this route was
   * entered — but a tab can sit on the dashboard for hours, and if the refresh token dies
   * out from under it (revoked, or the 30-day ceiling), `SessionProvider` reacts by writing
   * `me: null` straight into the query cache. Nothing about that write causes a NAVIGATION
   * by itself — the router does not watch the cache — so without this, the screen would
   * just sit there, mounted, silently unauthenticated, until the customer clicked something
   * and every request 403'd or 401'd in a confusing pile.
   *
   * `me === null` (not merely falsy) is the deliberate check: `undefined` is "still loading",
   * which every route already passed through on the way in and must not be mistaken for
   * "signed out".
   */
  useEffect(() => {
    if (me === null) void navigate({ to: "/sign-in" });
  }, [me, navigate]);

  if (!activeOrgId) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <LoadingBlock />
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: DashboardPage,
});

const sitesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sites",
  component: SitesPage,
});

const siteDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sites/$siteId",
  component: SiteDetailPage,
});

const monitoringRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/monitoring",
  component: MonitoringPage,
});

const devicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/devices",
  component: DevicesPage,
});

const deviceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/devices/$deviceId",
  component: DeviceDetailPage,
});

const refillsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/refills",
  component: RefillsPage,
});

const newRefillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/refills/new",
  validateSearch: (search: Record<string, unknown>): { siteId?: string; tankId?: string } => ({
    ...(typeof search["siteId"] === "string" ? { siteId: search["siteId"] } : {}),
    ...(typeof search["tankId"] === "string" ? { tankId: search["tankId"] } : {}),
  }),
  component: NewRefillPage,
});

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/requests",
  component: RequestsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  component: ReportsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});

/* ---------------------------------------------------------------- tree */

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([
    signInRoute,
    signUpRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
    invitationRoute,
  ]),
  onboardingRoute.addChildren([createOrgRoute, inviteTeamRoute, firstSiteRoute]),
  appRoute.addChildren([
    dashboardRoute,
    sitesRoute,
    siteDetailRoute,
    monitoringRoute,
    devicesRoute,
    deviceDetailRoute,
    refillsRoute,
    newRefillRoute,
    requestsRoute,
    reportsRoute,
    settingsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // The router does not cache tenant data — TanStack Query does, under org-scoped keys.
    defaultPreloadStaleTime: 0,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
